/**
 * Centralized Spot Querying System
 * 
 * This module serves as the single source of truth for all spot fetching logic.
 * It ensures consistency between UI interactions and direct URL loading.
 * 
 * All functions follow the same pattern:
 * 1. Parse/validate parameters
 * 2. Fetch spot IDs from appropriate endpoint
 * 3. Fetch full spot data using the canonical spots-ids endpoint
 * 4. Return standardized spot objects
 */
/**
 * Fetches minimal spot data for a given area (by id or slug)
 * Returns: { ids: [1,2,3], minimal: [{id, latitude, longitude, icon, color}, ...] }
 * @param {Object|number} areaIdOrParams - { areaId, areaSlug } or just areaId
 * @param {string} [areaSlug=null] - Optional area slug
 * @returns {Promise<Object>} { ids, minimal }
 */
export async function getMinimalSpotsForArea(areaIdOrParams, areaSlug = null) {
    let areaId = null;
    let slug = areaSlug;

    if (typeof areaIdOrParams === 'object' && areaIdOrParams !== null) {
        areaId = areaIdOrParams.areaId;
        slug = areaIdOrParams.areaSlug || slug;
    } else {
        areaId = areaIdOrParams;
    }

    if (!areaId) return { ids: [], minimal: [] };

    try {
        const { preloadMinimalForArea } = await import('./cache/index.js');
        const areaMap = await preloadMinimalForArea(areaId);
        const minimal = Array.from(areaMap.values());
        return {
            ids: minimal.map(s => s.id),
            minimal
        };
    } catch (error) {
        console.error('[Spot Queries] Error fetching minimal spots for area:', error);
        return { ids: [], minimal: [] };
    }
}

/**
 * Preloads minimal spot data for all areas at app startup
 * Returns: { [areaId]: { ids, minimal } }
 * @returns {Promise<Object>} Map of areaId -> { ids, minimal }
 */
export async function preloadAllAreasMinimalSpots() {
    const areas = await getAreasList();
    const results = {};
    await Promise.all(areas.map(async area => {
        const minimalData = await getMinimalSpotsForArea({ areaId: area.id, areaSlug: area.slug });
        results[area.id] = minimalData;
    }));
    return results;
}
import { DATA_PATHS } from '../dataPaths.js';
import { fetchSpotsByIds } from './spots-ids.js';
import { fetchWithCache } from './cache/data-cache.js';
import { getCurrentLanguage } from '../translations/translations.js';
import { EXPLORE_SETTINGS } from '/settings.js';

let _citiesCache = null;

/**
 * Fetches spots by their IDs (the canonical endpoint)
 * @param {Array<string|number>} ids - Array of spot IDs
 * @param {Object} options - Optional parameters
 * @param {string} options.lang - Language code (default: current language)
 * @param {boolean} options.favorites - Whether this is a favorites request
 * @returns {Promise<Array>} Array of spot objects
 */
export async function getSpotsByIds(ids, options = {}) {
    if (!Array.isArray(ids) || ids.length === 0) {
        return [];
    }

    const { lang = getCurrentLanguage(), favorites = false, map_id = null, areaSlug = null } = options;

    // Use spots cache for better performance (batch loading, caching, in-flight tracking)
    // Only skip cache for favorites (they have special handling)
    if (!favorites) {
        try {
            const { getSpot, batchLoadSpots } = await import('./cache/spots-cache.js');

            // For single spot, use getSpot
            if (ids.length === 1) {
                const spot = await getSpot(ids[0], { lang, map_id, areaSlug });
                return spot ? [spot] : [];
            }

            // For multiple spots, use batchLoadSpots
            const spotsMap = await batchLoadSpots(ids, { lang, map_id, areaSlug });
            // Convert Map to Array, preserving order
            return ids.map(id => spotsMap.get(Number(id))).filter(Boolean);
        } catch (error) {
            // Fallback to direct fetch if cache fails
            console.warn('[Spot Queries] Cache error, falling back to direct fetch:', error);
            return await fetchSpotsByIds(ids, { lang, favorites, map_id, areaSlug });
        }
    }

    // For favorites, use direct fetch (they have special endpoint)
    return await fetchSpotsByIds(ids, { lang, favorites, map_id, areaSlug });
}

/**
 * Fetches spots by search criteria (city + tags)
 * Used by both UI search and direct URL loading
 * @param {Object} params - Search parameters
 * @param {string} params.city - City slug
 * @param {Array<string>} params.tags - Array of tag slugs (optional)
 * @param {Object} options - Optional parameters
 * @param {string} options.lang - Language code (default: current language)
 * @returns {Promise<Array>} Array of spot objects
 */
export async function getSpotsBySearch(params, options = {}) {
    const { city, tags = [] } = params;
    const { lang = getCurrentLanguage() } = options;

    if (!city) {
        throw new Error('City parameter is required for search');
    }

    try {
        // Step 1: Get city ID from slug
        const cities = await fetchWithCache(DATA_PATHS.SEARCH_CITIES);
        const cityObj = cities.find(c => c.slug === city);

        if (!cityObj) {
            console.warn(`City not found: ${city}`);
            return [];
        }

        // Step 2: Use tag slugs directly (no mapping to IDs)
        let tagSlugs = Array.isArray(tags) ? tags : [];

        // Step 3: Get spot IDs from search results
        const searchParams = new URLSearchParams({
            cityId: cityObj.slug
        });
        if (tagSlugs.length > 0) {
            searchParams.append('tags', JSON.stringify(tagSlugs));
        }

        const spotsData = await fetchWithCache(`${DATA_PATHS.SEARCH_RESULTS}?${searchParams}`);

        if (spotsData.error) {
            throw new Error(spotsData.error);
        }

        const spotIds = spotsData.ids || [];

        // Step 4: Fetch full spot data using canonical endpoint
        return await getSpotsByIds(spotIds, { lang });

    } catch (error) {
        console.error('[Spot Queries] Error in getSpotsBySearch:', error);
        throw error;
    }
}

/**
 * Fetches spots by explore criteria (area + pack + tags)
 * Used by both UI explore and direct URL loading
 * @param {Object} params - Explore parameters
 * @param {string} params.area - Area slug
 * @param {string} params.pack - Pack slug
 * @param {Array<string>} params.tags - Array of tag slugs (optional)
 * @param {Object} options - Optional parameters
 * @param {string} options.lang - Language code (default: current language)
 * @returns {Promise<Array>} Array of spot objects
 */
export async function getSpotsByExplore(params, options = {}) {
    const { area, pack, tags = [], areaId, packId, tagIds: providedTagIds } = params;
    const { lang = getCurrentLanguage() } = options;

    if (!area || !pack) {
        throw new Error('Area and pack parameters are required for explore');
    }

    try {
        let areaObj, packObj, tagIds = [];

        // Step 1: Get area ID (skip if already provided)
        if (areaId) {
            areaObj = { id: areaId, slug: area };
        } else {
            const areas = await fetchWithCache(DATA_PATHS.EXPLORE_AREAS);
            areaObj = areas.find(a => a.slug === area);

            if (!areaObj) {
                console.warn(`Area not found: ${area}`);
                return [];
            }
        }

        // Step 2: Get pack ID (skip if already provided)
        if (packId) {
            packObj = { id: packId, slug: pack };
        } else {
            const packs = await fetchWithCache(`${DATA_PATHS.EXPLORE_PACKS}?areaId=${areaObj.id}`);
            packObj = packs.find(p => p.slug === pack);

            if (!packObj) {
                console.warn(`Pack not found: ${pack}`);
                return [];
            }
        }

        // Step 3: Get tag IDs (skip if already provided, or if tags provided as slugs)
        if (providedTagIds && Array.isArray(providedTagIds)) {
            tagIds = providedTagIds;
        } else if (tags.length > 0) {
            // Check if "all" or "default" tags are provided and if showing all spots is allowed
            const hasAllOrDefault = tags.some(tag => tag === 'all' || tag === 'default');
            if (hasAllOrDefault && !EXPLORE_SETTINGS.ALLOW_SHOW_ALL_SPOTS) {
                // If setting is OFF and user provided "all" or "default", return empty array
                console.warn('"all" or "default" tag provided but ALLOW_SHOW_ALL_SPOTS is disabled. Returning empty results.');
                return [];
            }

            // Use cached fetch to prevent duplicate requests
            const { fetchWithCache } = await import('./cache/index.js');
            const tagList = await getTagsForAreaPack(areaObj.id, packObj.id);
            const validTagSlugs = tagList.map(t => t.slug);

            // Check if all provided tags are valid
            const invalidTags = tags.filter(tag => !validTagSlugs.includes(tag) && tag !== 'all' && tag !== 'default');
            if (invalidTags.length > 0) {
                // If any tag is invalid, return empty array (don't show all spots)
                console.warn(`Invalid tags found: ${invalidTags.join(', ')}. Returning empty results.`);
                return [];
            }

            tagIds = tagList.filter(t => tags.includes(t.slug)).map(t => t.id);
        }

        // Step 4: Get spot IDs from explore results
        // Use filtered-ids cache helper (dedup + TTL + minimal fast-path)
        const { getFilteredIds } = await import('./cache/filtered-ids-cache.js');
        const spotIds = await getFilteredIds(areaObj.id, packObj.id, tagIds);

        // Step 5: Fetch full spot data using canonical endpoint
        return await getSpotsByIds(spotIds, { lang });

    } catch (error) {
        console.error('[Spot Queries] Error in getSpotsByExplore:', error);
        throw error;
    }
}

/**
 * Fetches favorite spots by their IDs
 * Used by both UI favorites and direct URL loading
 * @param {Array<string|number>} favIds - Array of favorite spot IDs
 * @param {Object} options - Optional parameters
 * @param {string} options.lang - Language code (default: current language)
 * @returns {Promise<Array>} Array of favorite spot objects
 */
export async function getSpotsByFavorites(favIds, options = {}) {
    const { lang = getCurrentLanguage() } = options;

    if (!Array.isArray(favIds) || favIds.length === 0) {
        return [];
    }

    try {
        return await getSpotsByIds(favIds, { lang, favorites: true });
    } catch (error) {
        console.error('[Spot Queries] Error in getSpotsByFavorites:', error);
        throw error;
    }
}

/**
 * Fetches a Discover map object by its slug.
 * @param {string} slug - Map slug
 * @returns {Promise<Object|null>} Map object or null if not found
 */
export async function getMapBySlug(slug) {
    if (!slug) return null;
    try {
        const map = await fetchWithCache(`/app_core/api/sqlite/get-map-by-slug.php?slug=${encodeURIComponent(slug)}`);
        if (!map || map.error) return null;
        return map;
    } catch (error) {
        console.error('[Spot Queries] Error fetching map by slug:', error);
        return null;
    }
}


/**
 * Fetches a Discover capsule object by its slug (legacy support).
 * @param {string} slug - Capsule slug
 * @returns {Promise<Object|null>} Capsule object or null if not found
 * @deprecated Use getMapBySlug instead for new maps structure
 */
export async function getCapsuleBySlug(slug) {
    if (!slug) return null;
    try {
        // First try new maps structure
        const map = await getMapBySlug(slug);
        if (map) return map;

        // Fallback to legacy capsules
        const capsule = await fetchWithCache(`/app_core/api/sqlite/get-capsule-by-slug.php?slug=${encodeURIComponent(slug)}`);
        if (!capsule || capsule.error) return null;
        return capsule;
    } catch (error) {
        console.error('[Spot Queries] Error fetching capsule by slug:', error);
        return null;
    }
}

/**
 * Fetches spots for a Discover map or capsule (by object or slug).
 * Supports both new maps structure (with spot_ids directly) and old capsules structure (with type).
 * If a slug is provided, fetches the map/capsule first.
 * @param {Object|string} mapOrCapsuleOrSlug - Map/capsule object or slug
 * @param {Object} options - Optional parameters
 * @param {string} options.lang - Language code (default: current language)
 * @returns {Promise<Array>} Array of spot objects
 */
export async function getSpotsByDiscover(mapOrCapsuleOrSlug, options = {}) {
    const { lang = getCurrentLanguage() } = options;
    let mapOrCapsule = mapOrCapsuleOrSlug;

    // If string, try to fetch (new maps first, then legacy capsules)
    if (typeof mapOrCapsuleOrSlug === 'string') {
        // Try new maps structure first
        mapOrCapsule = await getMapBySlug(mapOrCapsuleOrSlug);
        // If not found, try legacy capsules
        if (!mapOrCapsule) {
            mapOrCapsule = await getCapsuleBySlug(mapOrCapsuleOrSlug);
        }
        if (!mapOrCapsule) {
            console.warn('[Spot Queries] Map/Capsule not found for slug:', mapOrCapsuleOrSlug);
            return [];
        }
    }

    if (!mapOrCapsule) {
        console.warn('[Spot Queries] Invalid map/capsule:', mapOrCapsule);
        return [];
    }

    // New maps structure: has spot_ids directly (no type field)
    if (Array.isArray(mapOrCapsule.spot_ids)) {
        // Pass map_id so spots-ids.php can filter tags by maps_x_spots.tag_id
        const mapId = mapOrCapsule.id || null;
        return await getSpotsByIds(mapOrCapsule.spot_ids, { lang, map_id: mapId });
    }

    // Legacy capsules structure: has type field
    if (mapOrCapsule.type === 'explore' && mapOrCapsule.explore) {
        // Use slugs from backend-provided explore object (area_slug, pack_slug, tag_slugs)
        // This matches what getSpotsByExplore expects
        const { area_slug, pack_slug, tag_slugs = [] } = mapOrCapsule.explore;
        return await getSpotsByExplore({ area: area_slug, pack: pack_slug, tags: tag_slugs }, { lang });
    } else if (mapOrCapsule.type === 'ids' && Array.isArray(mapOrCapsule.spot_ids)) {
        return await getSpotsByIds(mapOrCapsule.spot_ids, { lang });
    } else {
        console.warn('[Spot Queries] Map/Capsule type not supported or missing data:', mapOrCapsule);
        return [];
    }
}

/**
 * Utility function to get cities list
 * @returns {Promise<Array>} Array of city objects
 */
export async function getCitiesList() {
    if (_citiesCache) return _citiesCache;
    try {
        const cities = await fetchWithCache(DATA_PATHS.CITIES_LIST);
        _citiesCache = cities.map(city => ({
            ...city,
            bounds: [
                city.bounds_south,
                city.bounds_west,
                city.bounds_north,
                city.bounds_east
            ]
        }));
        return _citiesCache;
    } catch (error) {
        console.error('[Spot Queries] Error fetching cities:', error);
        return [];
    }
}

/**
 * Utility function to get tags for a city
 * @param {string|number} cityId - City ID
 * @returns {Promise<Array>} Array of tag objects
 */
export async function getTagsForCity(cityId) {
    try {
        return await fetchWithCache(`${DATA_PATHS.SEARCH_TAGS}?city_id=${cityId}`);
    } catch (error) {
        console.error('[Spot Queries] Error fetching tags for city:', error);
        return [];
    }
}

/**
 * Utility function to get areas list
 * @returns {Promise<Array>} Array of area objects
 */
export async function getAreasList() {
    try {
        // Use cached fetch to prevent duplicate requests
        const { fetchWithCache } = await import('./cache/index.js');
        return await fetchWithCache(DATA_PATHS.EXPLORE_AREAS);
    } catch (error) {
        console.error('[Spot Queries] Error fetching areas:', error);
        return [];
    }
}

/**
 * Utility function to get packs for an area
 * @param {string|number} areaId - Area ID
 * @returns {Promise<Array>} Array of pack objects
 */
export async function getPacksForArea(areaId) {
    try {
        // Use cached fetch to prevent duplicate requests
        const { fetchWithCache } = await import('./cache/index.js');
        return await fetchWithCache(`${DATA_PATHS.EXPLORE_PACKS}?areaId=${areaId}`);
    } catch (error) {
        console.error('[Spot Queries] Error fetching packs for area:', error);
        return [];
    }
}

/**
 * Utility function to get tags for area+pack combination
 * Uses cache-first approach with batch loading
 * @param {string|number} areaId - Area ID
 * @param {string|number} packId - Pack ID
 * @returns {Promise<Array>} Array of tag objects
 */
export async function getTagsForAreaPack(areaId, packId) {
    try {
        // Use cached fetch to prevent duplicate requests
        const { getTagsForPack } = await import('./cache/index.js');
        return await getTagsForPack(Number(areaId), Number(packId));
    } catch (error) {
        console.error('[Spot Queries] Error fetching tags for area+pack:', error);
        return [];
    }
} 